import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { SearchSelect, type SSOption } from "../ui/SearchSelect";
import { FilePreviewModal } from "../shipment/FilePreviewModal";
import { fmtMoney } from "./PriceHistoryTable";
import { CURRENCY_OPTS, PAYMENT_METHODS, PAYMENT_METHOD_LABEL } from "./PaymentTermsFields";
import { parseTrNumber } from "../../lib/trNumber";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import "./Purchasing.css";

interface Props {
  poId: string;
  canEdit: boolean;       // taslak + iptal değil
  awardedQuoteId: string | null;
  defaultQty?: number | null;   // tek satırlı talebin miktarı → yeni teklife ön-doldurulur
  defaultCurrency: string;
}

const emptyForm = { supplierId: "", supplierName: "", qty: "", unitPrice: "", currency: "TRY", total: "", note: "", paymentMethod: "", termDays: "", installmentCount: "" };

export function QuotesPanel({ poId, canEdit, awardedQuoteId, defaultQty, defaultCurrency }: Props) {
  const qc = useQueryClient();
  const listQ = trpc.purchasing.quote.list.useQuery({ poId }, { enabled: !!poId, staleTime: 5_000 });
  const suppliersQ = trpc.supplier.list.useQuery({ page: 1, pageSize: 100, isActive: true }, { enabled: canEdit, staleTime: 60_000 });
  const createMut = trpc.purchasing.quote.create.useMutation();
  const updateMut = trpc.purchasing.quote.update.useMutation();
  const removeMut = trpc.purchasing.quote.remove.useMutation();
  const awardMut = trpc.purchasing.awardQuote.useMutation();
  const docUploadMut = trpc.purchasing.attachment.upload.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const docFileRef = useRef<HTMLInputElement>(null);
  const [uploadQuoteId, setUploadQuoteId] = useState<string | null>(null);
  const [docsExpanded, setDocsExpanded] = useState<Set<string>>(new Set());
  const toggleDocs = (id: string) => setDocsExpanded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  function pickDoc(quoteId: string) { setUploadQuoteId(quoteId); docFileRef.current?.click(); }
  function onDocPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadQuoteId) return;
    setActErr(null);
    const reader = new FileReader();
    reader.onload = () => docUploadMut.mutate({ poId, quoteId: uploadQuoteId, filename: file.name, dataBase64: String(reader.result) }, { onError: (er) => setActErr(er.message) });
    reader.readAsDataURL(file);
  }

  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [err, setErr] = useState<string | null>(null);
  const [actErr, setActErr] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | { title: string; message: string; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);

  const quotes = listQ.data ?? [];
  const supplierOptions = useMemo<SSOption[]>(() => (suppliersQ.data?.rows ?? []).map((s) => ({ value: s.id, label: s.name, tail: s.code })), [suppliersQ.data]);

  // Kıyaslama vurgusu: aynı döviz içinde en DÜŞÜK toplam + genel en UZUN vade (yalnız ≥2 teklif varsa anlamlı).
  const bestTotalIds = useMemo(() => {
    const ids = new Set<string>();
    const groups = new Map<string, Array<{ id: string; total: number }>>();
    for (const q of quotes) if (q.total != null && q.total > 0) { const a = groups.get(q.currency) ?? []; a.push({ id: q.id, total: q.total }); groups.set(q.currency, a); }
    for (const arr of groups.values()) {
      if (arr.length < 2) continue;
      const min = Math.min(...arr.map((x) => x.total));
      for (const x of arr) if (x.total === min) ids.add(x.id);
    }
    return ids;
  }, [quotes]);
  const longestTermIds = useMemo(() => {
    const withTerm = quotes.filter((q) => q.termDays != null && q.termDays > 0);
    if (withTerm.length < 2) return new Set<string>();
    const max = Math.max(...withTerm.map((q) => q.termDays!));
    return new Set(withTerm.filter((q) => q.termDays === max).map((q) => q.id));
  }, [quotes]);

  function openAdd() {
    setEditId(null);
    setForm({ ...emptyForm, currency: defaultCurrency, qty: defaultQty != null ? String(defaultQty).replace(".", ",") : "" });
    setErr(null); setAdding(true);
  }
  function openEdit(q: (typeof quotes)[number]) {
    setEditId(q.id);
    setForm({
      supplierId: q.supplierId ?? "", supplierName: q.supplierName,
      qty: q.qty != null ? String(q.qty).replace(".", ",") : "",
      unitPrice: String(q.unitPrice).replace(".", ","), currency: q.currency,
      total: q.total != null ? String(q.total).replace(".", ",") : "", note: q.note ?? "",
      paymentMethod: q.paymentMethod ?? "", termDays: q.termDays != null ? String(q.termDays) : "", installmentCount: q.installmentCount != null ? String(q.installmentCount) : "",
    });
    setErr(null); setAdding(true);
  }

  // Kayıtlı tedarikçi seçilince firma adını doldur; auto-total qty×unitPrice.
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const qtyNum = form.qty.trim() ? parseTrNumber(form.qty) : null;
  const priceNum = form.unitPrice.trim() ? parseTrNumber(form.unitPrice) : null;
  const autoTotal = qtyNum != null && priceNum != null ? qtyNum * priceNum : null;

  async function save() {
    setErr(null);
    const name = form.supplierName.trim() || (form.supplierId ? (supplierOptions.find((o) => o.value === form.supplierId)?.label ?? "") : "");
    if (!name) return setErr("Firma seçin ya da firma adı yazın.");
    if (!form.unitPrice.trim()) return setErr("Birim fiyat girin.");
    const price = parseTrNumber(form.unitPrice);
    if (!Number.isFinite(price) || price < 0) return setErr("Geçerli birim fiyat girin (TR: 12,50 / 1.234,56).");
    const qty = form.qty.trim() ? parseTrNumber(form.qty) : null;
    if (qty != null && (!Number.isFinite(qty) || qty < 0)) return setErr("Geçerli miktar girin.");
    const total = form.total.trim() ? parseTrNumber(form.total) : (autoTotal ?? undefined);
    const method = form.paymentMethod || null;
    const payload = {
      supplierName: name, supplierId: form.supplierId || null,
      qty: qty ?? null, unitPrice: price, currency: form.currency as "TRY" | "USD" | "EUR" | "GBP",
      total: total != null ? total : null, note: form.note.trim() || null,
      paymentMethod: method as "cash" | "transfer" | "credit_card" | "term" | null,
      termDays: method === "term" && form.termDays.trim() ? Math.floor(Number(form.termDays)) : null,
      installmentCount: method === "credit_card" && form.installmentCount.trim() ? Math.floor(Number(form.installmentCount)) : null,
    };
    try {
      if (editId) await updateMut.mutateAsync({ id: editId, ...payload });
      else await createMut.mutateAsync({ poId, ...payload });
      await qc.invalidateQueries();
      setAdding(false); setEditId(null);
    } catch (e) { setErr((e as Error).message); }
  }


  return (
    <div>
      <div className="po-att-head">
        <span className="po-att-title">Alınan Teklifler ({quotes.length})</span>
        <span className="sp" />
        {canEdit ? <button type="button" className="btn-md secondary" onClick={openAdd}><Icon name="plus" size={14} />Teklif Ekle</button> : null}
      </div>
      {actErr ? <div className="form-banner-err" style={{ marginBottom: 8 }}>{actErr}</div> : null}
      <input ref={docFileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" style={{ display: "none" }} onChange={onDocPick} />

      {/* Teklif ekle/düzenle formu */}
      {adding && canEdit ? (
        <div style={{ border: "1px solid var(--primary)", borderRadius: "var(--radius-md)", padding: 14, background: "var(--primary-subtle)", marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 10, color: "var(--primary)" }}>{editId ? "Teklifi Düzenle" : "Yeni Teklif"}</div>
          {err ? <div className="form-banner-err" style={{ marginBottom: 10 }}>{err}</div> : null}
          <div className="form-row">
            <div className="form-field">
              <label className="lbl">Kayıtlı Tedarikçi</label>
              <SearchSelect value={form.supplierId || null} options={supplierOptions}
                onChange={(v) => set({ supplierId: v ?? "", supplierName: v ? (supplierOptions.find((o) => o.value === v)?.label ?? form.supplierName) : form.supplierName })}
                placeholder="— Listeden seç (opsiyonel) —" searchPlaceholder="Tedarikçi ara…" clearable />
              <span className="help">Onaylanan seçmek için kayıtlı tedarikçi gerekir.</span>
            </div>
            <div className="form-field">
              <label className="lbl">veya Firma Adı <span className="req">*</span></label>
              <input className="form-input" value={form.supplierName} onChange={(e) => set({ supplierName: e.target.value })} maxLength={200} placeholder="Serbest firma adı" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field"><label className="lbl">Miktar</label>
              <input className="form-input" inputMode="decimal" value={form.qty} onChange={(e) => set({ qty: e.target.value.replace(/[^\d.,]/g, "") })} placeholder="örn. 100" /></div>
            <div className="form-field"><label className="lbl">Birim Fiyat <span className="req">*</span></label>
              <input className="form-input" inputMode="decimal" value={form.unitPrice} onChange={(e) => set({ unitPrice: e.target.value.replace(/[^\d.,]/g, "") })} placeholder="örn. 12,50" /></div>
            <div className="form-field" style={{ maxWidth: 120 }}><label className="lbl">Döviz</label>
              <select className="form-select" value={form.currency} onChange={(e) => set({ currency: e.target.value })}>{CURRENCY_OPTS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}</select></div>
          </div>
          <div className="form-row">
            <div className="form-field"><label className="lbl">Toplam</label>
              <input className="form-input" inputMode="decimal" value={form.total} onChange={(e) => set({ total: e.target.value.replace(/[^\d.,]/g, "") })}
                placeholder={autoTotal != null ? `otomatik: ${new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2 }).format(autoTotal)}` : "miktar × birim fiyat"} />
              <span className="help">Boş bırakılırsa miktar × birim fiyat.</span></div>
            <div className="form-field"><label className="lbl">Not</label>
              <input className="form-input" value={form.note} onChange={(e) => set({ note: e.target.value })} maxLength={500} placeholder="teslim süresi vb." /></div>
          </div>
          {/* Teklif ödeme koşulları — onaylanan seçilince siparişe aktarılır */}
          <div className="form-row">
            <div className="form-field"><label className="lbl">Ödeme Şekli</label>
              <select className="form-select" value={form.paymentMethod} onChange={(e) => set({ paymentMethod: e.target.value })}>
                <option value="">— Belirtilmedi —</option>
                {PAYMENT_METHODS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
              </select></div>
            {form.paymentMethod === "term" ? (
              <div className="form-field" style={{ maxWidth: 180 }}><label className="lbl">Vade (net gün)</label>
                <input className="form-input" type="number" min={0} max={3650} value={form.termDays} onChange={(e) => set({ termDays: e.target.value })} placeholder="örn. 30" /></div>
            ) : null}
            {form.paymentMethod === "credit_card" ? (
              <div className="form-field" style={{ maxWidth: 180 }}><label className="lbl">Taksit</label>
                <input className="form-input" type="number" min={1} max={60} value={form.installmentCount} onChange={(e) => set({ installmentCount: e.target.value })} placeholder="örn. 6" /></div>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn-md ghost" onClick={() => { setAdding(false); setEditId(null); }} disabled={createMut.isPending || updateMut.isPending}>Vazgeç</button>
            <button type="button" className="btn-md primary" onClick={save} disabled={createMut.isPending || updateMut.isPending}><Icon name="check" size={14} />{createMut.isPending || updateMut.isPending ? "Kaydediliyor…" : "Kaydet"}</button>
          </div>
        </div>
      ) : null}

      {quotes.length === 0 && !adding ? (
        <div className="po-att-empty">Henüz teklif yok{canEdit ? " — farklı firmalardan teklif ekleyip karşılaştırın, birini onaylanan seçin" : ""}.</div>
      ) : quotes.length > 0 ? (
        <div className="dt2-scroll">
          <table className="dt2 po-quote-table" style={{ fontSize: 12.5 }}>
            <thead>
              <tr>
                <th>Firma</th>
                <th className="num" style={{ width: 80 }}>Miktar</th>
                <th className="num" style={{ width: 110 }}>Birim Fiyat</th>
                <th className="num" style={{ width: 120 }}>Toplam</th>
                <th style={{ width: 130 }}>Ödeme Şekli</th>
                <th style={{ width: 110 }}>Vade</th>
                <th className="num" style={{ width: 56 }}>Dok.</th>
                {canEdit ? <th style={{ width: 128 }}>İşlem</th> : null}
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const won = awardedQuoteId === q.id;
                const low = bestTotalIds.has(q.id), long = longestTermIds.has(q.id);
                const expanded = docsExpanded.has(q.id);
                const vade = q.paymentMethod === "term" && q.termDays != null ? `${q.termDays} gün`
                  : q.paymentMethod === "credit_card" && q.installmentCount ? `${q.installmentCount} taksit` : "—";
                return (
                  <Fragment key={q.id}>
                    <tr className={won ? "po-q-won" : ""}>
                      <td>
                        {won ? <span className="badge-pill b-success" style={{ marginRight: 6 }}><Icon name="check" size={10} />Onaylanan</span> : null}
                        <span style={{ fontWeight: 600 }}>{q.supplierName}</span>{q.supplierId ? "" : <span style={{ color: "var(--neutral-400)" }}> (kayıtsız)</span>}
                        {q.note ? <span className="sec" style={{ fontSize: 10.5 }}>{q.note}</span> : null}
                      </td>
                      <td className="cell-num">{q.qty != null ? new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(q.qty) : "—"}</td>
                      <td className="cell-num">{fmtMoney(q.unitPrice, q.currency)}</td>
                      <td className="cell-num">{q.total != null ? <span className={low ? "po-best" : undefined} title={low ? "Dövizinde en düşük toplam" : undefined}>{fmtMoney(q.total, q.currency)}</span> : "—"}</td>
                      <td>{q.paymentMethod ? (PAYMENT_METHOD_LABEL[q.paymentMethod] ?? q.paymentMethod) : <span style={{ color: "var(--neutral-400)" }}>—</span>}</td>
                      <td>{vade === "—" ? <span style={{ color: "var(--neutral-400)" }}>—</span> : <span className={`mono ${long ? "po-best" : ""}`} title={long ? "En uzun ödeme vadesi" : undefined}>{vade}</span>}</td>
                      <td className="cell-num">
                        {q.docCount > 0
                          ? <button type="button" className="po-hist-toggle" onClick={() => toggleDocs(q.id)} title="Dokümanları göster/gizle"><Icon name={expanded ? "chevDown" : "chevRight"} size={11} />{q.docCount}</button>
                          : <span style={{ color: "var(--neutral-400)" }}>—</span>}
                      </td>
                      {canEdit ? (
                        <td>
                          <div className="row-acts">
                            <button type="button" className="iact info" style={{ position: "relative" }} aria-label="Doküman ekle" title="Teklif dokümanı ekle (foto / PDF)" disabled={docUploadMut.isPending} onClick={() => pickDoc(q.id)}>
                              <Icon name="upload" size={14} />{q.docCount > 0 ? <span className="po-doc-badge">{q.docCount}</span> : null}
                            </button>
                            <button type="button" className="iact success" aria-label="Onaylanan seç" title={q.supplierId ? "Onaylanan seç (tedarikçi + ödeme + fiyat işlenir)" : "Onaylanan seçmek için kayıtlı tedarikçi gerekir"} disabled={!q.supplierId || won || awardMut.isPending}
                              onClick={() => setConfirm({ title: "Onaylanan teklifi seç?", tone: "primary", confirmLabel: "Onaylanan Seç", message: `${q.supplierName} teklifi onaylanan seçilecek: siparişin tedarikçisi, dövizi, ödeme koşulları ve (tek kalemliyse) birim fiyatı bu tekliften doldurulacak.`, run: () => awardMut.mutate({ quoteId: q.id }, { onSuccess: () => qc.invalidateQueries(), onError: (e) => setActErr(e.message) }) })}>
                              <Icon name="check" size={14} />
                            </button>
                            <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => openEdit(q)}><Icon name="edit" size={14} /></button>
                            <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={() => setConfirm({ title: "Teklifi sil?", tone: "danger", confirmLabel: "Sil", message: `${q.supplierName} teklifi ve dokümanları silinecek.`, run: () => removeMut.mutate({ id: q.id }, { onSuccess: () => qc.invalidateQueries(), onError: (e) => setActErr(e.message) }) })}><Icon name="trash" size={14} /></button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                    {expanded ? (
                      <tr className="po-q-docrow">
                        <td colSpan={canEdit ? 8 : 7} style={{ background: "var(--neutral-50)" }}>
                          <QuoteDocRows poId={poId} quoteId={q.id} canEdit={canEdit} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <ConfirmDialog open={!!confirm} title={confirm?.title ?? ""} tone={confirm?.tone} confirmLabel={confirm?.confirmLabel} cancelLabel="Vazgeç"
        message={confirm?.message} onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }} onCancel={() => setConfirm(null)} />
    </div>
  );
}

// Bir teklifin dokümanları — satır (liste) düzeni: tür ikonu/önizleme + ad + yükleyen·tarih + görüntüle/sil.
function fmtDocDateTime(iso: string): string { return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }

function QuoteDocRows({ poId, quoteId, canEdit }: { poId: string; quoteId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const listQ = trpc.purchasing.attachment.list.useQuery({ poId, quoteId }, { staleTime: 5_000 });
  const removeMut = trpc.purchasing.attachment.remove.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const [preview, setPreview] = useState<string | null>(null);
  const rows = listQ.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="po-doc-list">
      {rows.map((a) => {
        const isPdf = a.kind === "document" || a.url.toLowerCase().endsWith(".pdf");
        return (
          <div key={a.id} className="po-doc-row">
            {isPdf
              ? <span className="po-doc-ic pdf"><Icon name="fileText" size={15} /></span>
              : <img className="po-doc-thumb" src={a.url} alt={a.fileName ?? "görsel"} />}
            <span className="po-doc-name" title={a.fileName ?? undefined}>{a.fileName ?? "dosya"}</span>
            <span className="po-doc-meta">{a.uploadedBy ? `${a.uploadedBy} · ` : ""}{fmtDocDateTime(a.createdAt)}</span>
            <button type="button" className="iact info" aria-label="Görüntüle" title="Görüntüle" onClick={() => setPreview(a.url)}><Icon name="eye" size={14} /></button>
            {canEdit ? <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={() => removeMut.mutate({ id: a.id })} disabled={removeMut.isPending}><Icon name="x" size={14} /></button> : null}
          </div>
        );
      })}
      <FilePreviewModal url={preview} onClose={() => setPreview(null)} />
    </div>
  );
}
